import { FC, ReactNode, useEffect, useState } from 'react';
import {
    Activity,
    ActivityResponse,
    ActivityTableRow,
    AuthActivity,
    DefaultDestinations,
    EntityData,
    LastAuthIdentity,
    LastRiskIdentity,
    LogsMetaData,
    RelyingParties,
    RiskActivity
} from '../types';
import jstz from 'jstimezonedetect';
import { httpGet } from '../../utils';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { EmptyStateIcons, Table, TwoLinesCell } from '@silverfort/sf-components';
import { HttpResponse } from '../../../api/types/Http';
import { INFINITE_SCROLL_BATCH_SIZE } from '@silverfort-nodelibs/common/utils/consts';
import moment from 'moment';
import { RISK_CHANGE_TYPE } from '@silverfort-nodelibs/common/utils/consts';
import { ACTIVITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';

import { POLICY_ACTION_TYPES } from '@silverfort-nodelibs/common/utils/consts';

import useFeature from '../../SystemSettings/hooks/featureFlags/useFeature';
import { FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import { destination, source, users } from '../../Controls/Filters/FiltersObjects';
import EntityActivitiesFilters from '../../Investigation/InvestigationComponents/EntityActivities/EntityActivitiesFilters';
import { RANGE_FILTER_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { buildActivityRows } from './IdentityDataActivityLogsBuilders';

const IdentityDataActivityLogs: FC<{
    entityData: EntityData;
    entityType: number;
    onEntityClick: (guid?: string, identifier?: string, type?: number) => void;
}> = ({ entityData, entityType, onEntityClick }) => {
    const [activitiesFilters, setActivitiesFilters] = useState({ range: [RANGE_FILTER_TYPES.TODAY.value] });
    const [entityActivities, setEntityActivities] = useState<Activity[]>([]);
    const [lastAuthIdentity, setLastAuthIdentity] = useState<LastAuthIdentity>();
    const [lastRiskIdentity, setLastRiskIdentity] = useState<LastRiskIdentity>();
    const [defaultDestinations, setDefaultDestinations] = useState<DefaultDestinations>({});
    const [lastPage, setLastPage] = useState(false);
    const [pageNumber, setPageNumber] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [logsMetadata, setLogsMetadata] = useState<LogsMetaData>();
    const [relyingParties, setRelyingParties] = useState<RelyingParties>({});
    const geoLocationEnabled = useFeature(FEATURE_FLAGS_NAMES.GeoLocationEnabled);
    const [abortSignal, setAbortSignal] = useState<AbortController | null>(null);

    const formatFilters = (filters) => {
        const formattedFilters: { [key: string]: string | string[] } = {};

        for (const filterId in filters) {
            if (filterId === source.id) {
                formattedFilters.includeSources = filters[source.id]?.map((source) => source.value);
            } else if (filterId === destination.id) {
                formattedFilters.includeDestinations = filters[destination.id]?.map((dest) => dest.value);
            } else if (filterId === users.id) {
                formattedFilters.includeUsers = filters[users.id]?.map((user) => user.value);
            } else {
                formattedFilters[filterId] = filters[filterId];
            }
        }

        return formattedFilters;
    };

    const buildActivityColumns = () => {
        return [
            {
                columnKey: 'timestamp',
                title: `Time`,
                columnClassName: 'min-w-[140px] max-w-[140px] !group-hover:text-[#333333]',
                render: (row) => {
                    return (
                        <div className="group-hover:text-[#333333] !cursor-default">
                            <TwoLinesCell
                                topText={moment(row.timestamp).format('MMM DD, YYYY')}
                                bottomText={moment(row.timestamp).format('hh:mm:ss A')}
                            />
                        </div>
                    );
                }
            },
            {
                columnKey: 'log',
                title: `Log`,
                render: (row) => <span className="flex flex-wrap">{row.log}</span>,
                columnClassName: 'min-w-[316px] max-w-[316px] !cursor-default'
            },
            {
                columnKey: 'type',
                title: `Type`,
                columnClassName: 'min-w-[180px] max-w-[180px] !cursor-default'
            }
        ] as {
            columnClassName: string;
            columnKey: keyof ActivityTableRow;
            title: string;
            render?: (row: ActivityTableRow) => ReactNode;
        }[];
    };

    const getActivities = async (overrideData = false) => {
        if (abortSignal) {
            console.log('previous request cancelled');
            abortSignal.abort();
        }

        const abortController = new AbortController();
        setAbortSignal(abortController);

        const params = {
            entityType: entityType,
            lastAuthIdentity: overrideData ? undefined : lastAuthIdentity,
            lastRiskIdentity: overrideData ? undefined : lastRiskIdentity,
            localTimeZone: encodeURIComponent(jstz.determine().name()),
            filters: formatFilters(activitiesFilters),
            entityGuid: entityData.details.guid,
            entityId: entityData.id,
            signal: abortController.signal
        };

        const requestUrl = '/investigate/activities';

        try {
            const response = (await httpGet(requestUrl, {
                [HTTP_OPTIONS.REQ_OPTIONS]: { params }
            })) as unknown as HttpResponse<ActivityResponse>;
            const data = response.data;
            setAbortSignal(null);

            for (const item of data.activity) {
                if (item.activityType === ACTIVITY_TYPES.RISK_CHANGE) {
                    const risk = item as RiskActivity;
                    if (risk.risk_indicator_type) {
                        risk.riskChangeType = RISK_CHANGE_TYPE.INDICATOR_CHANGE;
                    } else if (risk.old_risk_level) {
                        risk.riskChangeType = RISK_CHANGE_TYPE.LEVEL_CHANGE;
                    }
                } else if (item.activityType === ACTIVITY_TYPES.AUTH_LOG) {
                    const activity = item as AuthActivity;
                    if (!activity.effective_rule_action) {
                        activity.policyType = (item as AuthActivity).action
                            ? POLICY_ACTION_TYPES.ALLOW.value
                            : POLICY_ACTION_TYPES.DENY.value;
                    } else if (activity.effective_rule_action) {
                        activity.policyType = POLICY_ACTION_TYPES[activity.effective_rule_action.toUpperCase()].value;
                    }
                }
            }

            setEntityActivities((activities) => (overrideData ? data.activity : [...activities, ...data.activity]));

            if (data.lastAuthIdentity) {
                setLastAuthIdentity(data.lastAuthIdentity);
            }

            if (data.lastRiskIdentity) {
                setLastRiskIdentity(data.lastRiskIdentity);
            }

            if (data.defaultDestinations) {
                setDefaultDestinations(data.defaultDestinations);
            }

            if (data.activity?.length < INFINITE_SCROLL_BATCH_SIZE) {
                setLastPage(true);
            }

            if (data.relyingParties) {
                setRelyingParties(data.relyingParties);
            }

            if (!logsMetadata) {
                const response = await httpGet('/investigate/getMetadataForLogs');

                if (response && response.data) {
                    setLogsMetadata(response.data);
                }
            }

            setIsLoading(false);
        } catch (e) {
            setIsLoading(false);
            console.error('Failed to load entity activities');
        }
    };

    useEffect(() => {
        setLastPage(false);
        setIsLoading(true);
        setPageNumber(0);
        setLastAuthIdentity(undefined);
        setLastRiskIdentity(undefined);
        getActivities(true);
    }, [entityData, entityType, activitiesFilters]);

    useEffect(() => {
        if (!isLoading && !lastPage && pageNumber > 0) {
            setIsLoading(true);
            getActivities();
        }
    }, [pageNumber]);

    return (
        <>
            <EntityActivitiesFilters
                newDesign={true}
                lightMode={true}
                entityType={{ id: entityType }}
                onFiltersChanged={setActivitiesFilters}
                geoLocationEnabled={geoLocationEnabled || false}
            />
            <Table
                tableContainerClassName={'overflow-x-hidden no-scrollbar min-h-[250px] max-h-[calc(100vh-500px)]'}
                columns={buildActivityColumns()}
                rows={
                    buildActivityRows({
                        rows: entityActivities,
                        onEntityClick,
                        defaultDestinations,
                        relyingParties,
                        logsMetadata,
                        geoLocationEnabled,
                        entityType,
                        entityData
                    }) || []
                }
                loading={isLoading}
                expandableRowsMode
                expandedDataKey="data"
                disableHighlightedFirstColumn={true}
                onScrollEnd={() => !lastPage && setPageNumber(pageNumber + 1)}
                EmptyStateProps={{ title: 'No Logs', icon: EmptyStateIcons.NoItems }}
            />
        </>
    );
};

export default IdentityDataActivityLogs;
